import classNames from 'classnames';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';
import Button from '../Button';

import Icon from '../Icon';
import DropdownItem from './DropdownItem';

const propTypes = exact({
  allOptionText: PropTypes.string,
  blockName: PropTypes.string,
  fieldName: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  labelText: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
          value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        })
      ),
    })
  ).isRequired,
  selectedValues: PropTypes.array,
  fullScreenMobile: PropTypes.bool,
  showIcon: PropTypes.bool,
});

const defaultProps = {
  allOptionText: 'All',
  blockName: null,
  fullScreenMobile: false,
  showIcon: false,
  selectedValues: [],
};

class DropdownMultiple extends Component {
  constructor(props) {
    super(props);

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.toggleList = this.toggleList.bind(this);

    this.container = null;
    this.valueHasChanged = false;

    this.defaultOption = {
      text: props.allOptionText,
      value: '',
    };

    this.state = {
      isActive: false,
      selectedValues: this.props.selectedValues,
    };
  }

  componentWillReceiveProps(nextProps) {
    const { selectedValues } = nextProps;
    if (selectedValues === this.state.selectedValues) return;
    this.setState({ selectedValues });
  }

  toggleList() {
    const isActive = !this.state.isActive;
    this.setState({ isActive, selectedOption: undefined });

    if (isActive) document.addEventListener('click', this.handleDocumentClick);
    else document.removeEventListener('click', this.handleDocumentClick);

    if (!isActive && this.valueHasChanged) {
      this.valueHasChanged = false;
      this.props.handleChange(this.props.fieldName, this.state.selectedValues);
    }
  }

  handleDocumentClick(event) {
    if (!this.container) return;

    const clickedInContainer = this.container.contains(event.target);
    const clickedOnContainer = this.container === event.target;

    if (!clickedOnContainer && !clickedInContainer) this.toggleList();
  }

  handleOptionClick = (value) => {
    const defaultValue = this.defaultOption.value;
    const stateValues = this.state.selectedValues;

    let selectedValues = [];

    if (value !== defaultValue) {
      if (stateValues.includes(value)) selectedValues = stateValues.filter((x) => x !== value);
      else selectedValues = [...stateValues, value].filter((x) => x !== defaultValue);
    }

    if (selectedValues !== this.state.selectedValues) {
      this.valueHasChanged = true;
      this.setState({ selectedValues });
    }
  };

  handleClearClick = () => {
    const selectedValues = [];
    if (selectedValues !== this.state.selectedValues) {
      this.valueHasChanged = true;
      this.setState({ selectedValues });
    }
  };

  setSubOptionValue = (value, subOptionValue) => {
    setTimeout(() => {
      this.setState({
        selectedOption: this.state.selectedOption === value ? undefined : subOptionValue,
        isActive: true,
      });
    }, 50);
  };

  render() {
    const containerClasses = classNames(this.props.blockName, 'DropdownMultiple', {
      active: this.state.isActive,
      fullScreenMobile: this.props.fullScreenMobile,
    });

    let { selectedValues } = this.state;
    if (selectedValues.length < 1) selectedValues = [''];

    const options = [this.defaultOption, ...this.props.options];
    const filteredOptions = options.filter((option) => selectedValues.includes(option.value));
    const distinctFilteredOptions = filteredOptions.filter(function ({ text, value }) {
      const key = `${text}${value}`;
      return !this.has(key) && this.add(key);
    }, new Set());
    const selectedText = distinctFilteredOptions.map((option) => option.text).join(', ');

    const categories = [];
    options.forEach((option) => {
      if (option.category) {
        const category = categories.find((x) => x.text === option.category);
        if (category) {
          category.options.push(option);
        } else {
          categories.push({
            text: option.category,
            value: option.category,
            options: [option],
          });
        }
      }
    });

    return (
      <div
        className={containerClasses}
        ref={(element) => {
          this.container = element;
        }}
      >
        <button type="button" className="DropdownMultiple_button" onClick={this.toggleList}>
          <span className="DropdownMultiple_label">{this.props.labelText}</span>
          <div className="DropdownMultiple_value">{selectedText}</div>
        </button>
        <ul
          className={`DropdownMultiple_list ${
            this.props.fullScreenMobile ? 'DropdownMultiple_list-mobile' : ''
          }`}
        >
          {this.props.fullScreenMobile && (
            <li
              className="DropdownMultiple_item DropdownMultiple_subItem DropdownMultiple_mobileTitleContainer"
              onClick={this.toggleList}
            >
              <div className="DropdownMultiple_itemButton DropdownMultiple_mobileTitle">
                {this.props.labelText}
              </div>
              <Icon id="cross" blockName="DropdownMultiple_itemIcon" />
            </li>
          )}
          {categories.length === 0 &&
            options.map((option) => (
              <DropdownItem
                key={option.value}
                option={option}
                handleOptionClick={this.handleOptionClick}
                selectedValues={selectedValues}
                showIcon={this.props.showIcon}
              />
            ))}
          {categories.length > 0 &&
            categories.map((category) => (
              <DropdownItem
                key={category.value}
                option={category}
                handleOptionClick={this.handleOptionClick}
                selectedValues={selectedValues}
                showIcon={this.props.showIcon}
                selected={this.state.selectedOption === category.value}
                setSelectedOption={(value) => this.setSubOptionValue(category.value, value)}
              />
            ))}
          {this.props.fullScreenMobile && (
            <div className="DropdownMultiple_mobileActionBar">
              <Button
                text="Clear all"
                blockName="DropdownMultiple_mobileActionBarButton"
                handleClick={this.handleClearClick}
              />
              <Button
                text="Apply filters"
                blockName="DropdownMultiple_mobileActionBarButton"
                handleClick={this.toggleList}
              />
            </div>
          )}
        </ul>
      </div>
    );
  }
}

DropdownMultiple.propTypes = propTypes;
DropdownMultiple.defaultProps = defaultProps;

export default DropdownMultiple;
